import { useQuery } from "@tanstack/react-query";
import {
  WORKER_STATUS_BUSY_POLL_MS,
  WORKER_STATUS_POLL_MS,
  getWorkerHealth,
  latestWorkerSeenAt,
  usesClaudeTeam,
  type AiEngine,
  type WorkerHealth,
  type WorkersStatusResponse,
} from "@/lib/ai-engine";

export interface WorkerStatusView {
  /** True only when the workspace engine uses the Claude team (claude_worker or hybrid); otherwise nothing is fetched or shown. */
  enabled: boolean;
  /** The engine this status was requested for. */
  engine: AiEngine;
  /** Last successful response, if any. */
  data: WorkersStatusResponse | undefined;
  /** "unknown" until the first successful response (or when the status endpoint errors). */
  health: WorkerHealth | "unknown";
  lastSeenAt: string | null;
  queued: number;
  claimed: number;
  /** queued + claimed: how many jobs the Claude team has not finished yet. */
  active: number;
  isError: boolean;
  /** Show the "No worker has checked in recently" banner (never seen, or last seen > 10 min ago). */
  shouldWarn: boolean;
}

/**
 * Polls GET /api/workers/status every 30s (every 10s while jobs are queued or claimed) when the
 * workspace engine is claude_worker or hybrid. `workspaceId` is passed as a query param so the
 * server can scope the status; it is ignored if the server resolves the workspace from the session.
 */
export function useWorkerStatus(workspaceId: number | null | undefined, engine: AiEngine): WorkerStatusView {
  const enabled = usesClaudeTeam(engine) && !!workspaceId;

  const query = useQuery<WorkersStatusResponse>({
    queryKey: ["/api/workers/status", workspaceId ?? null],
    queryFn: async () => {
      const res = await fetch(`/api/workers/status?workspaceId=${encodeURIComponent(String(workspaceId))}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled,
    refetchInterval: enabled
      ? (q) => {
          const d = q.state.data;
          return d && (d.queued ?? 0) + (d.claimed ?? 0) > 0 ? WORKER_STATUS_BUSY_POLL_MS : WORKER_STATUS_POLL_MS;
        }
      : false,
    staleTime: WORKER_STATUS_BUSY_POLL_MS / 2,
  });

  const data = enabled ? query.data : undefined;
  const lastSeenAt = latestWorkerSeenAt(data);
  const health: WorkerHealth | "unknown" = data ? getWorkerHealth(lastSeenAt) : "unknown";
  const queued = data?.queued ?? 0;
  const claimed = data?.claimed ?? 0;

  return {
    enabled,
    engine,
    data,
    health,
    lastSeenAt,
    queued,
    claimed,
    active: queued + claimed,
    isError: enabled && query.isError && !data,
    shouldWarn: enabled && (health === "never" || health === "stale"),
  };
}
